<template lang="pug">
  .flooring-calculator
    .header
      Icon(name="flooring/rolls")
      h4 {{__('Calculate rolls needed')}}
    .subtext
        i {{__('Note: Individual rolls are 4’ in width, and 25’ or 50’ in length each. These figures are used to find the rolls needed based on total square footage.')}}
    swatches.calc-by(
      label="Calculate Rolls by:"
      :modelValue="calcBy"
      :options="[{label:'Total Square Ft.', value:'square-feet'}, {label:'Width x Length', value: 'width-length'}]"
      @update:modelValue="onCalcBy"
    )
    .calculate-flooring-by
      .height-length(v-if="calcBy === 'width-length'")
        .inputs
          RogueInput(label="Width (in ft.)" v-model="width" type="number")
          RogueInput(label="Length (in ft.)" v-model="length" type="number")
        .output
          .total
            p {{__('Total Square Ft.')}}
            p {{calcTotalSqFoot ? calcTotalSqFoot : '-'}}
          RogueButton(size="s" width="full" @click="calculate" :disabled="disabled") {{__('Calculate')}}
      .square-feet(v-else)
        RogueInput(label="Total Square Feet" v-model="totalSquareFt" type="number" placeholder="Min. Required: 100 sq ft")
        .btn-container
          RogueButton(size="s" width="full" @click="calculate" :disabled="disabled" :color="disabled && 'flat-disabled'") {{__('Calculate')}}
      Message.error(status="error" v-if="error")
        Icon.left-icon(name="alert-error")
        | {{error}}
      .options(v-if="options.length")
        p {{__('Calculated Options')}}
        .subtext {{__('The roll length options and recommended tape quantity are listed below:')}}
        Panel.options-panel
          Radio(
            v-for="option in options"
            :key="option.sku"
            :modelValue="selectedLength"
            name="Roll Length"
            :option="option.sku"
            @update:modelValue="selectOption"
          )
            .radio-container
              .name
                b {{option.label}}
                i {{option.sqft}} square feet
              Price(:product="option" :buy-request="optionBuyRequest(option)" theme="calculated-options")
</template>
<script>
import { nextTick } from 'vue'
import { createBuyRequest } from '@/js/helpers/buy-request'
import { calculateFlooringNeeded } from './utils'

export default {
  name  : 'FlooringCalculator',
  props : {
    rolls: {
      type    : Array,
      default : () => ([]),
    },
    buyRequest: {
      type     : Object,
      required : true,
    },
  },
  emits: ['selected'],
  data() {
    return {
      calcBy          : 'square-feet',
      options         : [],
      totalSquareFt   : null,
      calcTotalSqFoot : null,
      selectedLength  : '',
      length          : null,
      width           : null,
      output          : [],
      error           : null,
    }
  },
  computed: {
    disabled() {
      if (this.calcBy === 'square-feet') {
        return !this.totalSquareFt
      }

      return !this.length || !this.width
    },
  },
  watch: {
    buyRequest: {
      deep: true,
      handler(newVal) {
        if (!newVal.qty) {
          nextTick(() => {
            this.options = []
            this.totalSquareFt = null
            this.calcTotalSqFoot = null
            this.calcBy = null
            this.error = null
            setTimeout(() => {
              this.calcBy = 'square-feet'
            }, 500)
          })
        } else {
          this.options = calculateFlooringNeeded(this.calcTotalSqFoot, this.rolls)
          const currentLength = this.selectedLength?.split('-').pop() || '25'
          this.selectedLength = this.options.find(opt => opt.sku.indexOf(`-${currentLength}`) !== -1)?.sku
        }
      },
    },
  },
  methods: {
    onCalcBy(event) {
      this.calcBy = event
      this.totalSquareFt = null
      this.calcTotalSqFoot = null
      this.options = []
      this.error = null
    },
    selectOption(value) {
      this.selectedLength = value
      this.$emit('selected', this.options.find(option => option.sku === value))
    },
    optionBuyRequest(option) {
      const br = createBuyRequest(option, this.$rogue)
      br.qty = option.qty

      return br
    },
    calculate() {
      this.error = null
      if (this.calcBy !== 'square-feet') {
        this.calcTotalSqFoot = (parseInt(this.width, 10) * parseInt(this.length, 10)).toFixed(0)
      } else {
        this.calcTotalSqFoot = this.totalSquareFt
      }
      if (this.calcTotalSqFoot < 100) {
        this.calcTotalSqFoot = 100
        this.totalSquareFt = 100
        // this.error = 'Minimum square footage must be 100\''
        //
        // return
      }
      this.options = calculateFlooringNeeded(this.calcTotalSqFoot, this.rolls)
      this.selectedLength = this.options[0]?.sku || ''
      this.$emit('selected', this.options[0])
    },
  },
}
</script>
<style lang="scss" scoped>
.flooring-calculator {
  .header {
    display: flex;
    h4 {
      margin-left: 6px;
    }
  }
  .subtext {
    margin: 0 0 $size-xxs 0;
    font-size: $size-xs;
  }
  .options {
    p {
      margin-bottom: $space-xxs;
      font-weight: bold;
      text-transform: capitalize;
    }
    b {
      display: block;
      margin-bottom: $size-xxs;
    }
    .radio-container {
      display: flex;
      justify-content: space-between;
      .radio-container {
        background: transparent;
        border: none;
      }
      .name {
        b {
          font-size: $size-xs;
          line-height: $line-height-s;
        }
      }
    }
  }
  .calculate-flooring-by {
    .square-feet, .height-length .inputs, .output {
      display: flex;
      justify-content: space-between;
      align-items: center;
      > * {
        width: 48%;
      }
    }
    .btn-container {
      margin-top: 4px;
      :deep() {
        .flat-disabled {
          color: black;
        }
      }
    }
    .output {
      align-items: start;
      .total p {
        &:first-child {
          font-size: $size-xs;
          margin:  0;
        }
      }
      > * {
        &:first-child {
          width: 30%;
        }

        &:last-child {
          width: 68%;
        }
      }
    }
    .error {
      margin: $size-xs 0;
    }
  }
  .options-panel {
    :deep() {
      .panel-header {
        display: none;
      }

      .panel-content {
        padding: 0;
      }
    }
  }
}
</style>
